import { Toggle, ToggleContext, type ToggleRootBaseProps } from "@ark-ui/react";
import { List, X } from "@phosphor-icons/react";
import clsx from "clsx";
import * as React from "react";
import { logos } from "../../assets/assets.js";
import { IconButton } from "../icon-button/IconButton.js";

interface HeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  fixed?: boolean;
  withBorder?: boolean;
  transparent?: boolean;
  withRounded?: boolean;
}

interface HeaderLogoProps {
  src?: string;
  alt?: string;
  className?: string;
}

interface HeaderHamburgerProps extends ToggleRootBaseProps {
  className?: string;
}

interface HeaderComponent
  extends React.ForwardRefExoticComponent<
    HeaderProps & React.RefAttributes<HTMLDivElement>
  > {
  Logo: typeof HeaderLogo;
  Hamburger: typeof HeaderHamburger;
}

const Header = React.forwardRef<HTMLDivElement, HeaderProps>((props, ref) => {
  const {
    fixed = false,
    transparent = false,
    withRounded = false,
    withBorder = true,
    className,
    ...rest
  } = props;

  return (
    <div
      ref={ref}
      className={clsx(
        "ox-reset ox-Header",
        fixed && "is-fixed",
        transparent && "is-transparent",
        withBorder && "with-border",
        className,
      )}
      {...rest}
      data-rounded={withRounded}
    />
  );
}) as HeaderComponent;

const HeaderHamburger = React.forwardRef<
  HTMLButtonElement,
  HeaderHamburgerProps
>(({ className, ...rest }, ref) => {
  return (
    <Toggle.Root
      ref={ref}
      className={clsx("ox-reset ox-HeaderHamburger", className)}
      {...rest}
      asChild
    >
      <IconButton variant="tertiary" color="gray">
        <ToggleContext>
          {({ pressed }) => (pressed ? <X /> : <List />)}
        </ToggleContext>
      </IconButton>
    </Toggle.Root>
  );
});

const HeaderLogo = React.forwardRef<HTMLImageElement, HeaderLogoProps>(
  (
    { src = logos.product.hdfcLife, alt = "HDFC Life", className, ...rest },
    ref,
  ) => (
    <img
      ref={ref}
      src={src}
      alt={alt}
      className={clsx("ox-reset ox-HeaderLogo", className)}
      {...rest}
    />
  ),
);

Header.displayName = "Header";
HeaderLogo.displayName = "HeaderLogo";

Header.Logo = HeaderLogo;
Header.Hamburger = HeaderHamburger;

export { Header };
export type { HeaderProps, HeaderLogoProps, HeaderHamburgerProps };
